import './HeaderMenu.scss';

import React from 'react';
import { useHistory } from 'react-router-dom';
import { PropsWithJsxAttributes } from '../../../utils/types/PropsWithJsxAttributes';
import { cn } from '../../../utils/bem';
import { useTheme } from '@dataartdev/uikit/Theme';
import { PortalWithTheme } from '@dataartdev/uikit/PortalWithTheme';
import TagManager from 'react-gtm-module';
import { IMenu, IMenuType0, IMenuType1, IMenuType2 } from '@store/models.menu';
import { MenuItemType0 } from './components/MenuItemType0';
import { MenuItemType1 } from './components/MenuItemType1';
import { MenuItemType2 } from './components/MenuItemType2';
import { useIntl } from 'react-intl';

export const cnHeaderMenu = cn('HeaderMenu');
type Timeout = ReturnType<typeof setTimeout>;
type MenuProps = PropsWithJsxAttributes<
  {
    items: IMenu[];
    hoverState: boolean;
    onHover?: () => void;
    onBlur?: () => void;
    onHeaderMenuHandler: () => void;
    container?: Element;
    inverted?: boolean;
  },
  'nav'
>;

export const HeaderMenu: React.FC<MenuProps> = ({
  items,
  className,
  onHover,
  onBlur,
  container = window.document.body,
  hoverState: hovered,
  inverted,
  onHeaderMenuHandler,
}) => {
  const { theme } = useTheme();
  const timer = React.useRef<Timeout | null>(null);
  const history = useHistory();

  const mouseEnterHandler = () => {
    if (onHover) onHover();
    if (timer.current) {
      clearTimeout(timer.current);
    }
  };

  const mouseLeaveHandler = () => {
    timer.current = setTimeout(() => {
      if (onBlur) onBlur();
    }, 500);
  };

  const servicesHandler = (title: string, nodeUrl?: string) => async () => {
    if (!nodeUrl?.includes('services')) return;
    onHeaderMenuHandler();
    const service = title.trim().toLowerCase();
    let dataLayer = {
      dataLayer: {
        event: 'click_services',
        services:
          service === 'services'
            ? 'click_all_services'
            : `click_${service.split(' ').join('_')}`,
      },
    };
    try {
      TagManager.dataLayer(dataLayer);

      if (nodeUrl.includes('http')) {
        window.location.href = nodeUrl;
      } else {
        history.push(nodeUrl);
      }
    } catch (error) {
      error instanceof Error
        ? console.error(error.message)
        : console.error('Error with TagManager dataLayer:', error);
    }
  };

  const { locale } = useIntl();

  return (
    <nav className={cnHeaderMenu({ hovered }, [className])}>
      <ul className={cnHeaderMenu('List')}>
        {items.map(item => {
          switch (item.type) {
            case 0:
              return (
                <MenuItemType0
                  key={item.node.nodeUrl}
                  item={item as IMenuType0}
                  inverted={inverted}
                  mouseEnter={mouseEnterHandler}
                  mouseLeave={mouseLeaveHandler}
                  onHeaderMenu={onHeaderMenuHandler}
                />
              );
            case 1:
              return (
                <MenuItemType1
                  key={item.node.nodeUrl}
                  item={item as IMenuType1}
                  inverted={inverted}
                  mouseEnter={mouseEnterHandler}
                  mouseLeave={mouseLeaveHandler}
                  onHeaderMenu={onHeaderMenuHandler}
                  servicesHandler={servicesHandler}
                />
              );
            case 2:
              return (
                <MenuItemType2
                  key={item.node.nodeUrl}
                  item={item as IMenuType2}
                  inverted={inverted}
                  locale={locale}
                  mouseEnter={mouseEnterHandler}
                  mouseLeave={mouseLeaveHandler}
                  onHeaderMenu={onHeaderMenuHandler}
                />
              );
            default:
              return null;
          }
        })}
      </ul>

      {hovered && (
        <PortalWithTheme
          preset={theme}
          container={container}
          className={cnHeaderMenu('Overlay')}
        >
          <div className={cnHeaderMenu('Backdrop')} aria-label="Overlay" />
        </PortalWithTheme>
      )}
    </nav>
  );
};
